import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDashboardCounts, getMonthlyIncome } from "../../apis/dashboard";
import { LanguageContext } from "../../providers/LanguageProvider";
import BarChart from "../../components/PatientChart";
import { ChartData } from "chart.js";
import { getTopDashboardRole } from "../../utils/RoleUtil";
import AdminDashboard from "./AdminDashboard";
import DoctorDashboard from "./DoctorDashboard";
import FrontDeskDashboard from "./FrontDeskDashboard";
import NursingDashboard from "./NursingDashboard";

const LegacyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [totalPatients, setPatientCount] = useState<any>();
  const [thisMonthsVisitCount, setThisMonthsVisitCounts] = useState<any>();
  const [medicinesCount, setMedicinesCount] = useState<any>();
  const [monthlyIncome, setMonthlyIncome] = useState<any>();
  const [thisMonthsIncomeCount, setThisMonthsIncomeCount] = useState<any>();
  const [chartData, setChartData] =
    useState<ChartData<"bar", (number | [number, number] | null)[], unknown>>();
  const { translate: t } = useContext(LanguageContext);
  const { updateLanguage } = useContext(LanguageContext);

  useEffect(() => {
    fetchMonthlyIncome();
    const user = JSON.parse(window.localStorage.getItem("user") || "{}");
    updateLanguage(user.preferredLanguage);
    fetchDashboardCounts();
  }, [updateLanguage]);

  const fetchDashboardCounts = () => {
    getDashboardCounts().then((res: any) => {
      setPatientCount(res.data.totalPatients);
      setThisMonthsVisitCounts(res.data.monthVisitCount);
      setMedicinesCount(res.data.totalMedicines);
      setThisMonthsIncomeCount(res.data.monthVisitFee);
      setChartData(res.data.monthlyCount);
    });
  };

  const fetchMonthlyIncome = () => {
    getMonthlyIncome().then((res: any) => {
      setMonthlyIncome(res.data.totalIncome);
    });
  };

  return (
    <>
      <div>
        <div className="text-sm font-medium text-gray-500"></div>
        <ul className="grid grid-cols-1 gap-5 mt-3 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4">
          <li
            className="flex col-span-1 rounded-md shadow-sm cursor-pointer"
            onClick={() => navigate("/patients")}
          >
            <div className="flex items-center justify-center flex-shrink-0 w-16 text-sm font-medium text-white bg-pink-600 rounded-l-md">
              TP
            </div>
            <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
              <div className="flex-1 px-4 py-2 text-sm truncate">
                <span className="font-medium text-gray-900 hover:text-gray-600">
                  {t("total_patients")}
                </span>
                <p className="text-gray-500">{totalPatients}</p>
              </div>
            </div>
          </li>

          <li
            className="flex col-span-1 rounded-md shadow-sm cursor-pointer"
            onClick={() => navigate("/visit-register")}
          >
            <div className="flex items-center justify-center flex-shrink-0 w-16 text-sm font-medium text-white bg-purple-600 rounded-l-md">
              MV
            </div>
            <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
              <div className="flex-1 px-4 py-2 text-sm truncate">
                <span className="font-medium text-gray-900 hover:text-gray-600">
                  {t("this_months_visits")}
                </span>
                <p className="text-gray-500">{thisMonthsVisitCount}</p>
              </div>
            </div>
          </li>

          <li
            className="flex col-span-1 rounded-md shadow-sm cursor-pointer"
            onClick={() => navigate("/medicines")}
          >
            <div className="flex items-center justify-center flex-shrink-0 w-16 text-sm font-medium text-white bg-yellow-500 rounded-l-md">
              ML
            </div>
            <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
              <div className="flex-1 px-4 py-2 text-sm truncate">
                <span className="font-medium text-gray-900 hover:text-gray-600">
                  {t("medicine_list")}
                </span>
                <p className="text-gray-500">{medicinesCount}</p>
              </div>
            </div>
          </li>

          <li
            className="flex col-span-1 rounded-md shadow-sm cursor-pointer"
            onClick={() => navigate("/")}
          >
            <div className="flex items-center justify-center flex-shrink-0 w-16 text-sm font-medium text-white bg-green-500 rounded-l-md">
              MI
            </div>
            <div className="flex items-center justify-between flex-1 truncate bg-white border-t border-b border-r border-gray-200 rounded-r-md">
              <div className="flex-1 px-4 py-2 text-sm truncate">
                <span className="font-medium text-gray-900 hover:text-gray-600">
                  {t("this_months_income")}
                </span>
                <p className="text-gray-500">{monthlyIncome}</p>
              </div>
            </div>
          </li>
        </ul>
      </div>
      <div className="grid grid-cols-2 mt-2">
        {chartData && chartData.datasets && (
          <div className="py-2 bg-white border rounded-md">
            <BarChart data={chartData} />
          </div>
        )}
        <div></div>
      </div>
    </>
  );
};

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin Dashboard",
  doctor: "My Dashboard",
  frontdesk: "Front Desk",
  nursing: "Nursing",
};

const Dashboard: React.FC = () => {
  const { updateLanguage } = useContext(LanguageContext);
  const role = getTopDashboardRole();

  useEffect(() => {
    const user = JSON.parse(window.localStorage.getItem("user") || "{}");
    updateLanguage(user.preferredLanguage);
  }, [updateLanguage]);

  const title = ROLE_LABELS[role] ?? "Dashboard";

  const renderDashboard = () => {
    switch (role) {
      case "admin":
        return <AdminDashboard />;
      case "doctor":
        return <DoctorDashboard />;
      case "frontdesk":
        return <FrontDeskDashboard />;
      case "nursing":
        return <NursingDashboard />;
      default:
        return <LegacyDashboard />;
    }
  };

  return (
    <div>
      {role !== "default" && (
        <h2 className="text-lg font-semibold text-gray-700 mb-4">{title}</h2>
      )}
      {renderDashboard()}
    </div>
  );
};

export default Dashboard;
